import React from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
// Direct imports (not the @components / @utils barrels): the barrels pull in
// native-only modules that break under Jest (same avoidance as DeleteAccountModal).
import { Text } from '../../../components/atoms/text/Text'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { formatEventDay } from '../../../utils/events'
import type { KtEvent } from '@hooks'

interface EventConsentNoticeProps {
  event: Pick<KtEvent, 'publicPageUntil' | 'retentionMonths'>
}

/**
 * RGPD information shown right where the person signs up (before the click
 * that puts them on the list): what is visible, to whom, for how long, plus a
 * link to the privacy policy. Signing up itself is the acceptance (owner's
 * decision), so there is deliberately no checkbox. The figures come from the
 * API (`retentionMonths`, `publicPageUntil`) so the promise always matches the
 * server's real behaviour.
 */
export function EventConsentNotice({ event }: EventConsentNoticeProps) {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const navigation = useNavigation()
  const styles = getStyles(theme)

  const lines = [
    t('events.privacy.before', { date: formatEventDay(event.publicPageUntil, currentLanguage) }),
    t('events.privacy.after'),
    t('events.privacy.organizer'),
    event.retentionMonths === 1
      ? t('events.privacy.durationOne')
      : t('events.privacy.durationMany', { months: event.retentionMonths }),
  ]

  return (
    <View style={styles.container} testID="event-consent-notice">
      <Text type="bodyExtraSmallBold" color={theme.colors.secondary}>
        {t('events.privacy.title')}
      </Text>
      {lines.map((line) => (
        <View key={line} style={styles.line}>
          <View style={styles.dot} />
          <Text type="bodyExtraSmall" color={theme.colors.greyBlue} style={styles.lineText}>
            {line}
          </Text>
        </View>
      ))}
      <TouchableOpacity
        accessibilityRole="link"
        testID="event-privacy-link"
        // The policy is a public page: the sign-up screen stays behind it.
        onPress={() => navigation.navigate('PublicPrivacyPolicy' as never)}
      >
        <Text type="bodyExtraSmallBold" color={theme.colors.primary} style={styles.link}>
          {t('events.privacy.policy')}
        </Text>
      </TouchableOpacity>
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: { gap: theme.spacing.xs },
    line: { flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing.sm },
    dot: {
      width: 4,
      height: 4,
      borderRadius: 2,
      marginTop: 7,
      backgroundColor: theme.colors.greyBlue,
    },
    lineText: { flex: 1, lineHeight: 17 },
    link: { textDecorationLine: 'underline' },
  })
